import {
  type DynamicTablesExcelOptions,
  importDynamicTableSheetsFromExcel,
} from './dynamic-table-excel';
import { type ExcelValueConflict, mergeExcelValues } from './excel-import-conflicts';

export const importDynamicTableWorkbooks = async (
  files: File[],
  options: DynamicTablesExcelOptions,
) => {
  const sources: { source: string; tables: Record<string, string>[] }[] = [];
  for (const file of files) {
    try {
      sources.push({
        source: file.name,
        tables: await importDynamicTableSheetsFromExcel(file, options),
      });
    } catch (error) {
      throw new Error(
        `${file.name}: ${error instanceof Error ? error.message : 'The XLSX file could not be imported.'}`,
      );
    }
  }
  const conflicts: ExcelValueConflict[] = [];
  const values = options.tables.map((table, tableIndex) => {
    let offset = 0;
    const tables = sources.map((source) => {
      const imported = source.tables[tableIndex];
      if (!table.continuous) return { source: source.source, values: imported };
      let rowCount = 0;
      const shifted = Object.fromEntries(
        Object.entries(imported).map(([key, value]) => {
          const row = key.match(/^continuousRow(\d+)(.*)$/);
          if (!row) throw new Error(`${source.source}: Unrecognised table row.`);
          rowCount = Math.max(rowCount, Number(row[1]));
          return [`continuousRow${Number(row[1]) + offset}${row[2]}`, value];
        }),
      );
      offset += rowCount;
      return { source: source.source, values: shifted };
    });
    const merged = mergeExcelValues(tables, (key) => {
      const row = table.rows.find((row) => key.startsWith(row.key));
      const column = row && table.columns.find((column) => key === `${row.key}${column.key}`);
      return `${table.title}: ${row?.label || key}${column ? ` - ${column.label}` : ''}`;
    });
    conflicts.push(
      ...merged.conflicts.map((conflict) => ({
        ...conflict,
        key: `${tableIndex}:${conflict.key}`,
      })),
    );
    return merged.values as Record<string, string>;
  });
  return {
    status: 'ready' as const,
    values,
    conflicts,
    resolveConflicts: (selected: Record<string, unknown>) =>
      values.map((table, index) => ({
        ...table,
        ...Object.fromEntries(
          Object.entries(selected)
            .filter(([key]) => key.startsWith(`${index}:`))
            .map(([key, value]) => [key.slice(key.indexOf(':') + 1), String(value)]),
        ),
      })),
  };
};
